import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0115 from "../lesson-15/lesson";
import lesson0118 from "../lesson-18/lesson";
import lesson0119 from "../lesson-19/lesson";

const linkLesson0115 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0115.id);
const linkLesson0118 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0118.id);
const linkLesson0119 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0119.id);

const theory = ` <h3 class=sectionHead>Eigenmodes of a cavity</h3>
<p>Whatever the type of cavity treated, the problem of the field in a lossless problem without source consists in solving in the frequency domain:</p>
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>Δp</mi>
            <mo>+</mo>
            <msup>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mi>p</mi>
            <mo>=</mo>
            <mn>0</mn>
            <mspace width="10px">
            <mtext>in the volume</mtext>
         </math>
      <td class=eq-no>(1)
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂p</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂n</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
            <mspace width="10px">
            <mtext> on the boundaries </mtext>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
   </math>
   is the wave number and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
   </math>
   the direction normal to the surface. As discussed in <a href="${linkLesson0118}" target="_blank">Lesson 1.18</a> boundaries reduces the quantity of solutions of the wave equations. In a cavity, there are solutions only for discrete values of the frequency. These solutions are the eigenmodes (or proper modes) because they are the solutions of the problem without source. These are associated to the eigenfrequencies (i.e. proper frequencies) for which the system is free to vibrate. Any lossless cavity has its own eigenmodes. These eigenmodes can be found analytically for only some geometries such as the spherical (<a href="${linkLesson0115}" target="_blank">Lesson 1.15</a>), parallelepipedic (<a href="${linkLesson0118}" target="_blank">Lesson 1.18</a>) or cylindrical (<a href="${linkLesson0119}" target="_blank">Lesson 1.19</a>) cavities. When the cavity presents symmetries, some modes have the same natural frequency. They are said to be degenerate. As discussed in <a href="${linkLesson0118}" target="_blank">Lesson 1.18</a>, each mode is generally associated with three indices related to the three dimensions
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>y</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>z</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-open>(</mo>
      <mi>r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>z</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   or
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-open>(</mo>
      <mi>r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ϕ</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   for the parallelepipedic, cylindrical or spherical cavity respectively. For other geometries modes can be more or less numbered with three indices but this might be sometime intricate.
<h3 class=sectionHead>Mode density</h3>
<p>
   At low frequency, the modes are generally well discriminated, that is to say that their frequencies are significantly different from neighboring modes. When the frequency increases, the frequency density of the modes increases considerably at the limit, their number increases like the frequency to the power 3. The number of modes whose frequency is less than a value
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   naturally depends on the problem considered. However, an estimate is possible from the expression
</p>
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>N</mi>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mi>L</mi>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>πS</mi>
                  <msubsup>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πV</mi>
                  <msubsup>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <mn>3</mn>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   are the volume and area of the enclosure, respectively, and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>L</mi>
   </math>
   the total length of the edges. When the wavelength is small compared to the cavity dimensions (i.e.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>kL</mi>
      <mo class=MathClass-rel>></mo>
      <mo class=MathClass-rel>></mo>
      <mn>1</mn>
   </math>), only the last term can be kept and thus obtain an order of magnitude of the number of modes of natural frequency lower than
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
   </math>. This distribution of modes is plotted in <a href="#" data-img-src=${config.ASSETS_URL}/01/20/theory/figure-1.svg data-img-alt="Figure 1. Distribution of modes as a function of frequency for a parallelepipedal 9.59m long, 4.42m wide and 2.32m high cavity. In red theoretical model (Equation 3).">Figure 1</a>.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/01/20/theory/figure-1.svg" alt="Figure 1. Distribution of modes as a function of frequency for a parallelepipedal 9.59m long, 4.42m wide and 2.32m high cavity. In red theoretical model (Equation 3)."><figcaption class=caption>Figure 1. Distribution of modes as a function of frequency for a parallelepipedal 9.59m long, 4.42m wide and 2.32m high cavity. In red theoretical model (Equation 3).</figcaption></figure>
<h3 class=sectionHead>Schroeder frequency</h3>
<p>
   When the density of the modes is low, the cavity will respond to some particular frequencies. Modes are very discernible which, in the case of room acoustics, is not an advantage. On the contrary, when the density of the modes is high, due to damping, more than one mode will be exited by a single frequency. This what is called mode overlap. In that case modes are no more distinguishable and the field becomes more ore less uniform. This is called diffuse field. The limit of diffuse field occurs when the mean frequency difference between two successive modes is in the range of the 3 dB bandwidth of the modes. Writing
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Δf</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>3</mn>
      <mfrac>
         <mrow>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>N</mi>
            <mo class=MathClass-open>(</mo>
            <mi>f</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
      </mfrac>
   </math>
   and considering that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Δf</mi>
      <mo class=MathClass-rel>≈</mo>
      <mfrac>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mi>T</mi>
         </mrow>
      </mfrac>
   </math>, with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   the reverberation time, leads to the definition of the Schroeder frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>Schroeder</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>2000</mn>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <mi>V</mi>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
   </math>, frequency above which the field can be considered diffuse.
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt>[1]
   <dd>A. Chaigne and J. Kergomard. Acoustics of Musical Instruments. Springer, New York, 2016.
   <dt>[2]
   <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Ap-plications. MCGraw-Hill Book Company, Inc., New York, 1981.
   <dt>[3]
   <dd>E. Skudrzyk. The Foundations of Acoustics—Basic Mathematics and Basic Acoustics. Springer–Verlag, 1971.
   <dt>[4]
   <dd>S. Temkin. Elements of Acoustics. Wiley, New York, 1981.
</dl>`;

export default theory;
